import React, { useCallback, useEffect, useMemo, useState } from 'react';

import CoordinationTable from './CoordinationTable';
import CoordinationPending from './CoordinationPending';
import DayTypePicker from './DayTypePicker';
import * as api from './api';
import { useToasts } from './Toasts';
import { MEMBER_STATE, MONTH_STATUS, defaultMonth, monthFromLocation, shiftMonth, syncLocationMonth } from './constants';
import { LoadingMask } from './shared';

/**
 * 店長端彙整（步驟 3 的店長視角）：同一張長條表看全體劃假，加上完成進度與鎖定／解鎖／發布。
 *
 * ⛔ 這一頁刻意沒有預假順位、規則面板與機動補位指派 —— 那些在「4 排班預覽」，
 *    混進來就會把「誰劃完了、可以發布了嗎」這件事情淹掉。
 * ⚠️ 例外只有「每日人力統計列」：店長就是在這一頁做協調決定，缺不缺人必須當場看得到，
 *    所以它以 tfoot 長在班表同一張表的下方（⇒ 永遠對齊 1–31 欄、共用同一條水平捲軸）。
 *    數字來自後端 staffing payload ＝ 預覽頁同一支 StaffingEvaluator，⛔ 這裡不做任何判定。
 *
 * ⚠️ 版面鐵則（佳儀 2026-09-22：「太多卡片與小說明文字，資訊太多很雜亂好痛苦」）：
 *    只有三個區塊 —— 摘要磚（4 格）／排休協調／全員月表。
 *    **留下**：數字、狀態、可點的東西、缺額標示。
 *    **拿掉**：解釋規則怎麼運作的長句、教學性文字、重複講同一件事的句子。
 *    非留不可的規則說明一律收進「說明」按鈕或 title，⛔ 不可以再長回常駐的 sc-help 段落。
 */
const ScheduleCoordinationManage = () => {
    const [toastLayer, notify] = useToasts();
    const [period, setPeriod] = useState(monthFromLocation);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showAudits, setShowAudits] = useState(false);
    // 規則／判讀說明：⛔ 預設收起來。常駐的長說明是佳儀 2026-09-22 明確要求拿掉的東西。
    const [showHelp, setShowHelp] = useState(false);
    // 日別明細／覆寫面板釘在哪一格。⛔ 只是 UI 狀態，⛔ 不快取任何日別資料。
    const [dayTypeTarget, setDayTypeTarget] = useState(null);

    const load = useCallback(async (year, month, { silent } = {}) => {
        if (!silent) setLoading(true);
        try {
            setPayload(await api.fetchMonth(year, month, true));
        } catch (err) {
            notify('error', '讀取排班資料失敗，請重新整理頁面');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        syncLocationMonth(period.year, period.month);
        load(period.year, period.month);
    }, [period, load]);

    const runAction = useCallback(
        async (fn) => {
            setBusy(true);
            try {
                const res = await fn();
                await load(period.year, period.month, { silent: true });
                notify('success', res.message || '已完成');
            } catch (err) {
                notify('error', err.message);
                await load(period.year, period.month, { silent: true });
            } finally {
                setBusy(false);
            }
        },
        [load, notify, period]
    );

    /**
     * 待協調排休的決定。⚠️ 決定會改變人力數字與後續判定，所以一律重讀整份月資料，
     * ⛔ 不在前端自己推算下一次的清單長什麼樣。
     */
    const handleDecide = useCallback(
        (item, decision, period) =>
            runAction(() => api.decideLeave(item.uuid, item.date, decision, period)),
        [runAction]
    );

    const handleClearDecision = useCallback(
        (item) => runAction(() => api.clearDecision(item.uuid, item.date)),
        [runAction]
    );

    /* ------------------------------ 例假日／休息日自動分類 ------------------------------ */

    const closeDayType = useCallback(() => setDayTypeTarget(null), []);

    const handlePlanDayTypes = useCallback(() => {
        closeDayType();
        runAction(() => api.planDayTypes(period.year, period.month));
    }, [closeDayType, runAction, period]);

    const handleOverrideDayType = useCallback(
        (member, date, dayType, reason) => {
            closeDayType();
            runAction(() => api.overrideDayType(member.uuid, date.date, dayType, reason));
        },
        [closeDayType, runAction]
    );

    const handleRevertDayType = useCallback(
        (member, date) => {
            closeDayType();
            runAction(() => api.revertDayType(member.uuid, date.date));
        },
        [closeDayType, runAction]
    );

    const handleHrReview = useCallback(
        (member, date, note) => {
            closeDayType();
            runAction(() => api.markDayTypeHrReview(member.uuid, date.date, note));
        },
        [closeDayType, runAction]
    );

    const handleDayTypeCell = useCallback((member, date) => {
        setDayTypeTarget((prev) =>
            prev && prev.member.uuid === member.uuid && prev.date.date === date.date
                ? null
                : { member, date }
        );
    }, []);

    const changedMembers = useMemo(
        () => ((payload && payload.members) || []).filter((m) => m.state === MEMBER_STATE.CHANGED),
        [payload]
    );

    const pendingMembers = useMemo(
        () => ((payload && payload.members) || []).filter((m) => m.state === MEMBER_STATE.PENDING),
        [payload]
    );

    if (loading && !payload) {
        return (
            <div className="sc-card">
                {toastLayer}
                <div className="sc-empty">讀取中…</div>
            </div>
        );
    }

    if (!payload) {
        return (
            <div className="sc-card sc-card--accent-red">
                {toastLayer}
                <div className="sc-empty">目前無法取得排班資料，請重新整理頁面。</div>
            </div>
        );
    }

    const counts = payload.counts || {};
    const coordCounts = (payload.coordination || {}).counts || {};
    const decidedCount = ((payload.coordination || {}).decided || []).length;
    // 多職能又沒指定本月主要職能的人。⛔ 不靜默：少了這一段，職能欄少一個人會找不出原因。
    const dutyPending = (payload.staffing || {}).duty_pending || [];
    // 例假日／休息日自動分類。⛔ 只有這一頁（店長端）拿得到，員工劃假頁完全不受影響。
    const dayTypes = payload.day_types || {};
    const dayTypeCounts = dayTypes.counts || {};
    const dayTypeViolations = dayTypes.violations || [];
    const dayTypeCell = dayTypeTarget
        ? (dayTypes.by_cell || {})[`${dayTypeTarget.member.uuid}|${dayTypeTarget.date.date}`] || null
        : null;
    const status = payload.status;
    const statusBadge =
        status === MONTH_STATUS.PUBLISHED
            ? 'sc-badge--green'
            : status === MONTH_STATUS.LOCKED
            ? 'sc-badge--orange'
            : 'sc-badge--blue';

    const markTotal = (payload.members || []).reduce((sum, m) => sum + ((m.summary || {}).mark_count || 0), 0);
    const willPublish = (payload.members || []).filter(
        (m) => ((m.summary || {}).mark_count || 0) > 0 || m.state !== MEMBER_STATE.PENDING
    ).length;

    const handleLock = () => {
        if (!window.confirm(`確定要鎖定 ${payload.year}年${payload.month}月嗎？\n\n鎖定後所有員工都無法再新增、修改或取消劃假，直到你解除鎖定。`)) {
            return;
        }
        runAction(() => api.lockMonth(payload.year, payload.month));
    };

    const handleUnlock = () => {
        if (!window.confirm(`確定要解除 ${payload.year}年${payload.month}月的鎖定嗎？\n\n解除後員工可以再次修改自己的劃假。`)) {
            return;
        }
        runAction(() => api.unlockMonth(payload.year, payload.month));
    };

    const handlePublish = () => {
        const warn = [
            `確定要發布 ${payload.year}年${payload.month}月的正式班表嗎？`,
            '',
            `・尚未完成：${counts.pending || 0} 人`,
            `・完成後有異動：${counts.changed_after_done || 0} 人`,
            '',
            '發布後員工端該月會變成唯讀，臨時變更需改走請假流程。',
            '發布只會處理有參與本月協調的人員，不會動到其他人或其他月份的既有班表。',
        ].join('\n');

        if (!window.confirm(warn)) return;

        runAction(() => api.publishMonth(payload.year, payload.month));
    };

    return (
        <div>
            {toastLayer}

            <header className="sched-head">
                <div className="sched-head__main">
                    <h1 className="sched-head__title">
                        劃假彙整
                        <span className="sched-head__period">
                            {payload.year} 年 {payload.month} 月
                        </span>
                        <span className={`sc-badge ${statusBadge}`}>{payload.status_label}</span>
                    </h1>
                </div>
                <div className="sched-head__actions">
                    <button
                        type="button"
                        className="sc-btn"
                        onClick={() => setPeriod(shiftMonth(payload.year, payload.month, -1))}
                    >
                        ← 上個月
                    </button>
                    <button type="button" className="sc-btn" onClick={() => setPeriod(defaultMonth())}>
                        本月
                    </button>
                    <button
                        type="button"
                        className="sc-btn"
                        onClick={() => setPeriod(shiftMonth(payload.year, payload.month, 1))}
                    >
                        下個月 →
                    </button>
                </div>
            </header>

            {/* 摘要磚只留四格，排序＝「要不要行動」。
                ⛔ 純資訊的數字不獨立成磚（併進 note 或 title），佳儀 2026-09-22：
                   「太多卡片與小說明文字，資訊太多很雜亂」。 */}
            <div className="sc-stats">
                <div
                    className={`sc-stats__item ${
                        coordCounts.pending ? 'sc-stats__item--orange' : 'sc-stats__item--muted'
                    }`}
                >
                    <span className="sc-stats__label">待協調排休</span>
                    <span className="sc-stats__value" data-coord-pending-count>
                        {coordCounts.pending || 0}
                        <small>筆</small>
                    </span>
                    <span className="sc-stats__note">
                        已自動採納 {coordCounts.auto || 0} 筆
                        {decidedCount ? `　·　你決定 ${decidedCount} 筆` : ''}
                    </span>
                </div>
                <div
                    className={`sc-stats__item ${
                        dayTypeViolations.length ? 'sc-stats__item--orange' : 'sc-stats__item--muted'
                    }`}
                >
                    <span className="sc-stats__label">法定下限未達</span>
                    <span className="sc-stats__value" data-count-violations>
                        {dayTypeViolations.length}
                        <small>項</small>
                    </span>
                    <span className="sc-stats__note">
                        {dayTypeViolations.length ? '會擋住發布' : dutyPending.length ? '另有職能待指定' : '可以發布'}
                    </span>
                </div>
                <div
                    className={`sc-stats__item ${
                        counts.pending || counts.changed_after_done
                            ? 'sc-stats__item--orange'
                            : 'sc-stats__item--green'
                    }`}
                    title={`預計發布 ${willPublish} 人　·　本月劃假共 ${markTotal} 筆`}
                >
                    <span className="sc-stats__label">劃假完成</span>
                    <span className="sc-stats__value" data-count-done>
                        {counts.done || 0}
                        <small>/ {counts.total || 0} 人</small>
                    </span>
                    <span className="sc-stats__note">
                        尚未 {counts.pending || 0} 人　·　完成後有異動 {counts.changed_after_done || 0} 人
                    </span>
                </div>
                <div
                    className={`sc-stats__item ${
                        dayTypes.has_plan ? 'sc-stats__item--blue' : 'sc-stats__item--muted'
                    }`}
                >
                    <span className="sc-stats__label">例假日／休息日</span>
                    <span className="sc-stats__value">
                        {dayTypes.has_plan ? (
                            <React.Fragment>
                                <span data-count-regular-holiday>{dayTypeCounts.regular_holiday || 0}</span>
                                <small>／</small>
                                <span data-count-rest-day>{dayTypeCounts.rest_day || 0}</span>
                                <small>天</small>
                            </React.Fragment>
                        ) : (
                            '—'
                        )}
                    </span>
                    <span className="sc-stats__note">
                        {dayTypes.has_plan ? (
                            <React.Fragment>
                                具名 {dayTypeCounts.named_leave || 0}
                                　·　店長調整 <span data-count-manual>{dayTypeCounts.manual || 0}</span>
                            </React.Fragment>
                        ) : (
                            '尚未分類'
                        )}
                    </span>
                </div>
            </div>

            {status === MONTH_STATUS.PUBLISHED && (
                <div className="sc-note sc-note--blue">
                    <strong>已發布</strong>
                    {payload.published_at
                        ? `（${payload.published_at}${payload.published_by ? ' 由 ' + payload.published_by : ''}）`
                        : ''}
                    。員工端該月已成唯讀。
                </div>
            )}

            {status === MONTH_STATUS.LOCKED && (
                <div className="sc-note sc-note--orange">
                    <strong>已鎖定</strong>
                    {payload.locked_at
                        ? `（${payload.locked_at}${payload.locked_by ? ' 由 ' + payload.locked_by : ''}）`
                        : ''}
                    。員工暫時無法修改劃假。
                </div>
            )}

            {/* 「誰還沒劃完」「誰完成後又改過」：原本一張卡 ＋ 一段名單，
                現在收成一行可展開的明細 —— 表格的「狀態」欄本來就看得到每個人的狀態。 */}
            {(pendingMembers.length > 0 || changedMembers.length > 0) && (
                <details className="sc-more" data-progress-detail>
                    <summary>
                        劃假進度明細（尚未完成 {pendingMembers.length} 人
                        {changedMembers.length ? `・完成後有異動 ${changedMembers.length} 人` : ''}）
                    </summary>
                    <div className="sc-more__body">
                        {pendingMembers.length > 0 && status === MONTH_STATUS.DRAFTING && (
                            <p>
                                <strong>尚未按「我劃完了」：</strong>
                                {pendingMembers.map((m) => m.name).join('、')}
                            </p>
                        )}
                        {changedMembers.length > 0 && (
                            <table className="sc-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '160px' }}>完成後又改過</th>
                                        <th style={{ width: '170px' }}>最後異動</th>
                                        <th style={{ width: '90px' }}>次數</th>
                                        <th>最後一筆</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {changedMembers.map((m) => (
                                        <tr key={m.uuid} className="is-warning">
                                            <td>{m.name}</td>
                                            <td className="is-nowrap">{m.changed_after_done_at || '—'}</td>
                                            <td className="is-num">{m.change_after_done_count}</td>
                                            <td style={{ color: 'var(--sc-ink-2)' }}>
                                                {m.last_change_summary || '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </details>
            )}

            <CoordinationPending
                coordination={payload.coordination}
                busy={busy}
                onDecide={handleDecide}
                onClear={handleClearDecision}
            />

            {/* 月表 ＝ 這一頁的主體。例假／休息日分類原本是獨立一張卡（一大段說明 ＋ 六個數字），
                現在只留「動作」在卡頭、「數字」在上面的摘要磚、「顏色」在表尾圖例 ——
                講的是同一張表的同一件事，⛔ 不再拆成兩張卡。 */}
            <section className="sc-card" data-day-type-panel>
                <div className="sc-card__head">
                    <h2 className="sc-card__title">
                        {payload.year} 年 {payload.month} 月全員月表
                        {dayTypeViolations.length > 0 && (
                            <span className="sc-badge sc-badge--orange">
                                法定下限未達 {dayTypeViolations.length} 項
                            </span>
                        )}
                    </h2>
                    <div className="sc-card__act">
                        <button
                            type="button"
                            className="sc-btn sc-btn--primary sc-btn--xs"
                            data-action="plan-day-types"
                            disabled={busy}
                            onClick={handlePlanDayTypes}
                        >
                            {dayTypes.has_plan ? '重新分類例假／休息日' : '自動分類例假／休息日'}
                        </button>
                        <button
                            type="button"
                            className={`sc-btn sc-btn--xs${showHelp ? ' is-on' : ''}`}
                            data-toggle="table-help"
                            onClick={() => setShowHelp((v) => !v)}
                        >
                            說明
                        </button>
                    </div>
                </div>

                {/* 規則與判讀方式：⛔ 不常駐在畫面上。內容是原本鋪在卡片與表尾的那幾段。 */}
                {showHelp && (
                    <div className="sc-card__body sc-card__body--tight" data-panel="table-help">
                        <div className="sc-help" style={{ margin: 0 }}>
                            <p>
                                <strong>例假／休息日分類</strong>：員工只標「希望排休／特休」，例假日與休息日
                                一律由系統在這裡自動分類 —— 先從已協調完成的排休中挑出最少必要的例假日
                                （候選排序：無診次 → 已核准全天排休 → 職務人力足夠 → 輪替公平 → 最少改動），
                                其餘全部歸休息日。半天 ⛔ 不算完整的例假或休息日；特休等具名假別 ⛔ 不會被
                                轉成例假或休息日。點任何一格可以看分類理由並逐格調整；重新分類只更新系統
                                建議值，店長調過的格子 ⛔ 不會被蓋掉。
                            </p>
                            <p>
                                <strong>每日人力</strong>：表格最下方是「在班總人數」與各職能各一列，格子寫
                                「在班／需求」（沒有需求的時段只寫在班人數），上下午數字相同時合併成一個。
                                低於需求會標橘底紅字。⚠️ 一個人每個時段只算進一個職能，所以各職能相加
                                可能少於總人數 —— 沒有職能、或多職能又沒指定「本月主要職能」的人
                                不計入任何職能。機動人力沒被指派補位時算在他自己的本月主要職能。
                                數字與「4 排班預覽」是同一份計算。
                            </p>
                            <p style={{ marginBottom: 0 }}>
                                <strong>發布</strong>：會把劃假寫成正式班表（沒劃假的日子＝上班），並保留
                                特休／一般休與全天／半天資訊。只處理有參與本月協調的人員，⛔ 不會動到
                                其他人或其他月份的既有班表。
                            </p>
                        </div>
                    </div>
                )}

                <CoordinationTable
                    payload={payload}
                    showStaffing
                    rankScope="all"
                    showCoordination
                    showDayTypes
                    onCellClick={handleDayTypeCell}
                    activeCellKey={
                        dayTypeTarget
                            ? `${dayTypeTarget.member.uuid}|${dayTypeTarget.date.date}`
                            : null
                    }
                />

                {/* 圖例：分類過就畫正式日別，沒分類過才畫假別色票 ——
                    ⛔ 兩套字樣不可以同時出現，會互相打架。 */}
                <div
                    className="sc-card__foot"
                    style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}
                >
                    {(dayTypes.has_plan ? dayTypes.legend || [] : payload.category_options || []).map((l) => (
                        <span key={l.value} title={l.effect || undefined}>
                            <i
                                style={{
                                    display: 'inline-block',
                                    width: '9px',
                                    height: '9px',
                                    borderRadius: '2px',
                                    marginRight: '4px',
                                    verticalAlign: '-1px',
                                    backgroundColor: l.color,
                                    border: l.value === 'work' ? '1px solid #cfd6dd' : 'none',
                                }}
                            />
                            {l.short === '班' ? '（空白）' : l.short ? l.short + ' ＝ ' + l.label : l.label}
                        </span>
                    ))}
                    <span>
                        <i
                            style={{
                                display: 'inline-block',
                                width: '9px',
                                height: '9px',
                                borderRadius: '2px',
                                marginRight: '4px',
                                verticalAlign: '-1px',
                                backgroundColor: '#fff',
                                border: '1px solid #cfd6dd',
                            }}
                        />
                        {dayTypes.has_plan ? '粗黑外框 ＝ 店長調整過' : '沒有標記＝上班'}
                    </span>
                </div>

                {dayTypeViolations.length > 0 && (
                    <div className="sc-note sc-note--orange" data-day-type-violations style={{ margin: '12px 16px' }}>
                        <strong>會擋住發布：</strong>
                        <ul>
                            {dayTypeViolations.slice(0, 12).map((v, i) => (
                                <li key={`${v.rule}-${v.user_uuid}-${v.window}-${i}`}>
                                    <span className="sc-badge sc-badge--grey">{v.rule_label}</span> {v.message}
                                </li>
                            ))}
                        </ul>
                        {dayTypeViolations.length > 12 && (
                            <div style={{ marginTop: '4px' }}>…另有 {dayTypeViolations.length - 12} 項。</div>
                        )}
                    </div>
                )}

                {dutyPending.length > 0 && (
                    <div className="sc-note sc-note--orange" data-duty-pending style={{ margin: '12px 16px' }}>
                        <strong>{dutyPending.length} 位同仁的「本月主要職能」待指定：</strong>
                        {dutyPending.map((d) => d.name).join('、')}
                        。未指定前不計入任何職能，發布也會被擋下（到「0 排班基礎設定 → 人員屬性」指定）。
                    </div>
                )}

                <div
                    className="sc-card__foot"
                    style={{ background: '#fff', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}
                >
                    {status === MONTH_STATUS.DRAFTING && (
                        <button
                            type="button"
                            className="sc-btn sc-btn--danger"
                            data-action="lock-month"
                            disabled={busy}
                            onClick={handleLock}
                        >
                            鎖定月份
                        </button>
                    )}
                    {status === MONTH_STATUS.LOCKED && (
                        <button
                            type="button"
                            className="sc-btn"
                            data-action="unlock-month"
                            disabled={busy}
                            onClick={handleUnlock}
                        >
                            解除鎖定
                        </button>
                    )}
                    <button
                        type="button"
                        className="sc-btn sc-btn--primary"
                        data-action="publish-month"
                        disabled={busy}
                        onClick={handlePublish}
                    >
                        {status === MONTH_STATUS.PUBLISHED ? '重新發布正式班表' : '發布正式班表'}
                    </button>
                    <span data-publish-summary>
                        預計發布 <b>{willPublish}</b> 人　·　本月劃假共 <b>{markTotal}</b> 筆
                    </span>
                    <button
                        type="button"
                        className={`sc-btn sc-btn--xs${showAudits ? ' is-on' : ''}`}
                        style={{ marginLeft: 'auto' }}
                        data-toggle="audits"
                        onClick={() => setShowAudits((v) => !v)}
                    >
                        異動紀錄 {(payload.audits || []).length}
                    </button>
                </div>

                {showAudits && (
                    <div className="sc-scroll" data-panel="audits">
                        {(payload.audits || []).length === 0 ? (
                            <div className="sc-empty">—</div>
                        ) : (
                            <table className="sc-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '150px' }}>時間</th>
                                        <th style={{ width: '160px' }}>操作者</th>
                                        <th style={{ width: '110px' }}>動作</th>
                                        <th>內容</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(payload.audits || []).map((a) => (
                                        <tr key={a.id}>
                                            <td className="is-nowrap">{a.at}</td>
                                            <td>{a.actor}</td>
                                            <td>
                                                <span className="sc-badge sc-badge--grey">{a.action_label}</span>
                                            </td>
                                            <td style={{ color: 'var(--sc-ink-2)' }}>{a.summary}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </section>

            {dayTypeTarget && (
                <DayTypePicker
                    anchorSelector={`[data-cell="${dayTypeTarget.member.uuid}|${dayTypeTarget.date.date}"]`}
                    target={{ ...dayTypeTarget, cell: dayTypeCell }}
                    options={dayTypes.manager_options || []}
                    busy={busy}
                    onOverride={handleOverrideDayType}
                    onRevert={handleRevertDayType}
                    onHrReview={handleHrReview}
                    onClose={closeDayType}
                />
            )}

            {busy && <LoadingMask>處理中...</LoadingMask>}
        </div>
    );
};

export default ScheduleCoordinationManage;
